import React, { useState, useMemo } from 'react';
import * as I from './ToolIcons';
import { toolsData, allToolsMap, type Tool } from '../data/toolsData';
import SmartSearch from './SmartSearch';
import { useAuth } from '../contexts/AuthContext';
import SEO from './SEO';

const SmallLogoIcon = () => (
  <svg width="24" height="24" viewBox="0 0 48 48" fill="none" xmlns="http://www.w3.org/2000/svg">
    <circle cx="24" cy="24" r="5" fill="#FACC15"/>
    <line x1="24" y1="9" x2="24" y2="17" stroke="white" strokeWidth="2"/>
    <circle cx="24" cy="7" r="2" fill="white"/>
    <line x1="24" y1="31" x2="24" y2="39" stroke="white" strokeWidth="2"/>
    <circle cx="24" cy="41" r="2" fill="white"/>
    <line x1="9" y1="24" x2="17" y2="24" stroke="white" strokeWidth="2"/>
    <circle cx="7" cy="24" r="2" fill="white"/>
    <line x1="31" y1="24" x2="39" y2="24" stroke="white" strokeWidth="2"/>
    <circle cx="41" cy="24" r="2" fill="white"/>
  </svg>
);

const XCircleIcon = () => <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 14l2-2m0 0l2-2m-2 2l-2-2m2 2l2 2m7-2a9 9 0 11-18 0 9 9 0 0118 0z" /></svg>;
const StarIcon = ({ filled }: { filled?: boolean }) => <svg xmlns="http://www.w3.org/2000/svg" className={`h-4 w-4 ${filled ? "text-yellow-400 fill-current" : "text-gray-500"}`} viewBox="0 0 20 20" fill="currentColor"><path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" /></svg>;

const TABS = ['All', 'Clinical', 'Retail', 'Reference'];

const AllToolsTabIcon = () => <I.SmallIcon><path strokeLinecap="round" strokeLinejoin="round" d="M4 6a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2H6a2 2 0 01-2-2V6zM14 6a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2h-2a2 2 0 01-2-2V6zM4 16a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2H6a2 2 0 01-2-2v-2zM14 16a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2h-2a2 2 0 01-2-2v-2z" /></I.SmallIcon>;

const TAB_ICONS: { [key: string]: React.ReactNode } = {
    'All': <AllToolsTabIcon />,
    'Clinical': <I.ClinicalTabIcon />,
    'Retail': <I.RetailTabIcon />,
    'Reference': <I.ReferenceTabIcon />,
};

// Category Theme Colors
const CATEGORY_THEMES: Record<string, string> = {
    Clinical: 'group-hover:border-blue-500/50 group-hover:shadow-blue-500/20',
    Retail: 'group-hover:border-emerald-500/50 group-hover:shadow-emerald-500/20',
    Reference: 'group-hover:border-purple-500/50 group-hover:shadow-purple-500/20',
};

interface AllToolsProps {
  quickAccessTools: string[];
  setActiveTool: (toolName: string | null) => void;
  onOpenEditQuickAccess: () => void;
  onNavigate: (view: any) => void;
  uid: string | null;
  onSelectDrug: (drugId: string) => void;
  onSelectGuide: (topic: string, source: string) => void;
}

const ToolCard: React.FC<{ tool: Tool; category: string; onClick: () => void; isQuickAccess: boolean }> = ({ tool, category, onClick, isQuickAccess }) => {
    const themeClass = CATEGORY_THEMES[category] || 'group-hover:border-yellow-400/50';
    const IconComponent = tool.icon;

    return (
        <button 
            onClick={onClick} 
            className={`
                relative flex flex-col items-center justify-center p-4 
                bg-[#15243E] border border-blue-900/30 rounded-2xl 
                transition-all duration-300 hover:scale-105 hover:bg-[#1E2E4A] shadow-sm
                group h-32 w-full ${themeClass} hover:shadow-lg
            `}
        >
            {isQuickAccess && (
                <div className="absolute top-2 right-2 opacity-50">
                    <StarIcon filled />
                </div>
            )}
            
            <div className="transform transition-transform duration-300 group-hover:scale-110 mb-3">
                <IconComponent />
            </div>
            
            <p className="text-xs font-semibold text-gray-300 group-hover:text-white text-center leading-tight line-clamp-2 px-1">
                {tool.name}
            </p>
            
            <div className="absolute bottom-0 left-0 w-full h-1 bg-gradient-to-r from-transparent via-white/10 to-transparent opacity-0 group-hover:opacity-100 transition-opacity" />
        </button>
    );
};

const AllTools: React.FC<AllToolsProps> = ({ quickAccessTools, setActiveTool, onOpenEditQuickAccess, onNavigate, uid, onSelectDrug, onSelectGuide }) => {
  const { setIsUpgradeModalOpen, subscription } = useAuth();
  const [activeTab, setActiveTab] = useState('All');

  const handleToolClick = (toolName: string) => {
    setActiveTool(toolName);
  };

  // Flatten tools and add category metadata
  const allToolsWithMeta = useMemo(() => {
      const list: { tool: Tool, category: string }[] = [];
      Object.entries(toolsData).forEach(([cat, tools]) => {
          tools.forEach(tool => list.push({ tool, category: cat }));
      });
      return list.sort((a, b) => a.tool.name.localeCompare(b.tool.name));
  }, []);

  // Filter Logic
  const displayedTools = useMemo(() => {
      let filtered = allToolsWithMeta;

      // 1. Filter by Tab
      if (activeTab !== 'All') {
          filtered = filtered.filter(item => item.category === activeTab);
      }

      return filtered;
  }, [allToolsWithMeta, activeTab]);

  // Group for rendering if in "All" tab
  const groupedForDisplay = useMemo(() => {
      if (activeTab !== 'All') return null; // Don't group if specific tab
      return toolsData;
  }, [activeTab]);

  return (
    <div className="text-white pt-2 pb-24 min-h-screen">
      <SEO 
        title="All Clinical Tools - Dr.Rx"
        description="Complete list of pharmacy calculators and tools including DDI Checkers, Renal Dosing, IV Compatibility, and more."
        keywords={["Pharmacy Tools", "Clinical Calculators", "DDI Checker", "Drug Interaction", "Renal Dosing", "Pharmacy App"]}
        url="/?view=all-tools"
      />
      
      {/* Header & Nav */}
      <div className="sticky top-0 z-40 bg-[#101D35]/95 backdrop-blur-xl border-b border-blue-900/50 px-4 -mx-4 sm:-mx-6 lg:-mx-8 sm:px-6 lg:px-8 pb-4 pt-4 shadow-lg">
          <div className="flex justify-between items-center mb-4 max-w-7xl mx-auto">
               <h1 className="text-2xl font-bold flex items-center gap-3">
                    <SmallLogoIcon />
                    Clinical Library
               </h1>
               <button onClick={() => onNavigate('library')} className="text-xs font-bold text-gray-400 hover:text-white bg-white/5 hover:bg-white/10 px-3 py-1.5 rounded-lg transition-colors">
                    Close
               </button>
          </div>

          {/* Smart Search */}
          <div className="mb-4">
             <SmartSearch 
                uid={uid}
                onSelectDrug={onSelectDrug}
                onSelectTool={setActiveTool}
                onSelectGuide={(topic, source) => {
                    if (subscription?.plan === 'Free' || subscription?.plan === 'Trial') {
                            setIsUpgradeModalOpen(true);
                    } else {
                            onNavigate('clinical-guides'); 
                            onSelectGuide(topic, source);
                    }
                }}
                placeholder="Search 50+ tools (e.g., 'CrCl', 'IV', 'Pill')..."
             />
          </div>

          {/* Tabs */}
          <div className="flex items-center justify-center gap-2 overflow-x-auto no-scrollbar max-w-3xl mx-auto">
            {TABS.map(tab => {
                const isActive = activeTab === tab;
                return (
                <button
                    key={tab}
                    onClick={() => setActiveTab(tab)}
                    className={`flex items-center justify-center gap-2 px-4 py-2 rounded-full font-bold text-xs sm:text-sm transition-all duration-200 whitespace-nowrap ${
                        isActive 
                        ? 'bg-yellow-400 text-gray-900 shadow-lg shadow-yellow-400/20 scale-105' 
                        : 'bg-[#1E2E4A] text-gray-400 hover:bg-white/10 hover:text-white border border-transparent'
                    }`}
                >
                    {TAB_ICONS[tab]}
                    <span>{tab}</span>
                </button>
                )
            })}
        </div>
      </div>

      <div className="max-w-7xl mx-auto mt-6">
        {/* Case 1: Grouped Display (All Tab) */}
        {groupedForDisplay ? (
             <div className="space-y-10 tab-content-enter">
                 {/* Quick Access Section */}
                 {quickAccessTools.length > 0 && (
                    <section>
                         <div className="flex justify-between items-end mb-4 px-2">
                            <h2 className="text-lg font-bold text-white flex items-center gap-2">
                                <StarIcon filled /> Favorites
                            </h2>
                            <button onClick={onOpenEditQuickAccess} className="text-xs font-bold text-blue-300 hover:text-white transition-colors">
                                Edit
                            </button>
                         </div>
                         <div className="grid grid-cols-2 sm:grid-cols-4 md:grid-cols-5 lg:grid-cols-6 gap-3 sm:gap-4">
                            {quickAccessTools.map(toolName => {
                                const tool = allToolsMap.get(toolName);
                                if(!tool) return null;
                                // Find category for styling
                                const category = Object.keys(toolsData).find(cat => toolsData[cat].some(t => t.name === toolName)) || 'Clinical';
                                return <ToolCard key={tool.name} tool={tool} category={category} onClick={() => handleToolClick(tool.name)} isQuickAccess={true} />
                            })}
                         </div>
                    </section>
                 )}

                 {/* Categories */}
                 {Object.entries(toolsData).map(([category, tools]) => (
                     <section key={category}>
                         <h2 className={`text-lg font-bold mb-4 px-2 border-l-4 pl-3 ${
                             category === 'Clinical' ? 'border-blue-500 text-blue-100' :
                             category === 'Retail' ? 'border-emerald-500 text-emerald-100' :
                             'border-purple-500 text-purple-100'
                         }`}>
                             {category} Tools
                         </h2>
                         <div className="grid grid-cols-2 sm:grid-cols-4 md:grid-cols-5 lg:grid-cols-6 gap-3 sm:gap-4">
                             {tools.map(tool => (
                                 <ToolCard 
                                    key={tool.name} 
                                    tool={tool} 
                                    category={category} 
                                    onClick={() => handleToolClick(tool.name)} 
                                    isQuickAccess={quickAccessTools.includes(tool.name)}
                                 />
                             ))}
                         </div>
                     </section>
                 ))}
             </div>
        ) : (
            /* Case 2: Filtered List (Specific Tab) */
            <div className="tab-content-enter">
                {displayedTools.length > 0 && (
                     <>
                        <p className="text-xs font-bold text-gray-500 uppercase tracking-wider mb-4 px-2">
                            {`${activeTab} Tools`}
                        </p>
                        <div className="grid grid-cols-2 sm:grid-cols-4 md:grid-cols-5 lg:grid-cols-6 gap-3 sm:gap-4">
                            {displayedTools.map(item => (
                                <ToolCard 
                                    key={item.tool.name} 
                                    tool={item.tool} 
                                    category={item.category} 
                                    onClick={() => handleToolClick(item.tool.name)} 
                                    isQuickAccess={quickAccessTools.includes(item.tool.name)}
                                />
                            ))}
                        </div>
                     </>
                )}
            </div>
        )}

      </div>
    </div>
  );
};

export default AllTools;